import { getRandomPartialColor } from '#lib/helper/color.js';
import { isDateInValidityDates } from '#lib/helper/date.js';
import { generateUUID } from '#lib/helper/uuid.js';
import { t } from '#lib/stores/i18n.js';
import { CurrencyCode, formatCurrency } from '#lib/helper/currency.js';
import { promoCodeStore } from '#lib/stores/promoCode.js';
import { now } from '#lib/stores/time.js';
import {
	DiscountType,
	type PromoCode,
	type SavePromoCodeRequest
} from '#lib/types/generated/index.js';
import type { DateTime } from 'luxon';
import { get } from 'svelte/store';

export function toSavePromoCodeRequest(promoCode: PromoCode): SavePromoCodeRequest {
	return {
		code: promoCode.code,
		discountType: promoCode.discountType,
		discountValue: promoCode.discountValue,
		color: promoCode.color,
		isActive: promoCode.isActive,
		newCustomersOnly: promoCode.newCustomersOnly,
		applyToAllBookings: promoCode.applyToAllBookings,
		applyToGiftcard: promoCode.applyToGiftcard,
		applyToPackage: promoCode.applyToPackage,
		applyToOffers: promoCode.applyToOffers,
		applyToSessions: promoCode.applyToSessions,
		applyToActivities: promoCode.applyToActivities,
		applyToProducts: promoCode.applyToProducts,
		description: promoCode.description,
		validityDates: promoCode.validityDates,
		publishStart: promoCode.publishStart ? promoCode.publishStart.toISO() : null,
		publishEnd: promoCode.publishEnd ? promoCode.publishEnd.toISO() : null,
		usageLimit: promoCode.usageLimit,
		limitPerCustomer: promoCode.limitPerCustomer,
		minAmount: promoCode.minAmount
	};
}

export function isPromoCodeUnique(code: string, currentPromoCodeId: string): boolean {
	const existingPromoCode = get(promoCodeStore).find(
		(p) => p.code.toUpperCase() === code.toUpperCase() && p.id !== currentPromoCodeId
	);
	return !existingPromoCode;
}

export function isPromoCodeValidAt(promo: PromoCode, date: DateTime): boolean {
	const start = promo.validityStart ?? null;
	if (start && date < start) {
		return false;
	}

	const end = promo.validityEnd ?? null;
	if (end && date > end) {
		return false;
	}

	return isDateInValidityDates(promo.validityDates, date);
}

export function createNewPromoCode(): PromoCode {
	return {
		id: generateUUID(),
		code: '',
		description: '',
		discountType: DiscountType.Percentage,
		discountValue: 0,
		validityStart: null,
		validityEnd: null,
		validityDates: null,
		usageLimit: null,
		limitPerCustomer: null,
		minAmount: null,
		newCustomersOnly: false,
		applyToAllBookings: true,
		applyToOffers: [],
		applyToSessions: [],
		applyToActivities: [],
		applyToProducts: [],
		applyToGiftcard: false,
		applyToPackage: false,
		isActive: true,
		color: getRandomPartialColor(),
		createdAt: get(now),
		publishStart: null,
		publishEnd: null,
		archivedAt: null
	};
}

export function formatDiscount(promoCode: PromoCode): string {
	const type = promoCode.discountType;
	const value = promoCode.discountValue;

	if (type === DiscountType.Percentage) {
		return `-${value}%`;
	} else if (type === DiscountType.AmountPerBooking) {
		return `-${formatCurrency(value, CurrencyCode.EUR)}`;
	} else {
		return `-${formatCurrency(value, CurrencyCode.EUR)}/${get(t)('pax-short')}`;
	}
}
